/* ===========================================================
   Wiredan App - Stable 1.6.9 (UUID & DYNAMIC ACCESS ENABLED)
   Path: src/App.js
   Status: STABLE | CAMERA-READY | LOGISTICS-READY | TRACKING-READY | MUSIC-STUDIO-GATEWAY-ACTIVE
   ============================================================ */

import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useSatellite } from './context/SatelliteContext'; 
import { VaultProvider } from './context/VaultContext'; 
import toast from 'react-hot-toast'; 
import api from './utils/api'; 

// CORE UI
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import BottomNav from './components/BottomNav';
import AdminSidebar from './components/AdminSidebar';
import GlobalBanner from './components/GlobalBanner'; 

// LAZY LOAD CORE PAGES
const VaultOrder = lazy(() => import('./pages/VaultOrder'));
const LocalVault = lazy(() => import('./pages/LocalVault'));
const GlobalVault = lazy(() => import('./pages/GlobalVault'));
const OrderDetails = lazy(() => import('./pages/OrderDetails'));
const SystemHealth = lazy(() => import('./pages/SystemHealth')); 
const ErrorMonitor = lazy(() => import('./components/ErrorMonitor'));
const HomePage = lazy(() => import('./pages/HomePage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const EducationHub = lazy(() => import('./pages/EducationHub'));
const TradeMarketplace = lazy(() => import('./pages/TradeMarketplace'));
const OrderTracking = lazy(() => import('./pages/OrderTracking'));
const AdminNexus = lazy(() => import('./pages/AdminNexus')); 
const RootDashboard = lazy(() => import('./pages/admin/RootDashboard')); 
const DisputeCenter = lazy(() => import('./pages/admin/DisputeCenter')); 
const ActivityLog = lazy(() => import('./pages/admin/ActivityLog')); 
const SecurityAudit = lazy(() => import('./pages/SecurityAudit')); 
const AdminVault = lazy(() => import('./pages/AdminVault')); 
const AdminRevenue = lazy(() => import('./pages/AdminRevenue')); 
const TransactionHistory = lazy(() => import('./pages/TransactionHistory'));
const Settings = lazy(() => import('./pages/Settings')); 
const MarketplaceCreate = lazy(() => import('./pages/MarketplaceCreate'));
const ProductListing = lazy(() => import('./pages/ProductListing'));
const IdentityVerification = lazy(() => import('./pages/IdentityVerification'));
const TradeVerify = lazy(() => import('./pages/TradeVerify'));
const EscrowSession = lazy(() => import('./pages/EscrowSession'));
const SellerDashboard = lazy(() => import('./pages/SellerDashboard'));
const Dispute = lazy(() => import('./pages/Dispute'));
const GlobalMarket = lazy(() => import('./pages/GlobalMarket'));
const FrontendDebug = lazy(() => import('./pages/FrontendDebug'));
const AgriSetup = lazy(() => import('./pages/AgriSetup'));

// --- SURGICAL ADDITIONS: CAMERA, LOGISTICS, TRACKING & MUSIC STUDIO GATEWAY IMPORTS ---
const DanAiCamera = lazy(() => import('./pages/DanAiCamera'));
const LogisticsDashboard = lazy(() => import('./pages/LogisticsDashboard'));
const BuyerTrackingMap = lazy(() => import('./pages/BuyerTrackingMap'));
const WiredanHubGateway = lazy(() => import('./components/WiredanHubGateway')); // 🎵 Route Studio & Hub through Subscription Gateway

const LoadingSpinner = () => (
  <div className="flex flex-col justify-center items-center h-screen bg-[#F5F2F0] text-[#F25C05]">
    <div className="w-12 h-12 border-4 border-current border-t-transparent rounded-full animate-spin"></div>
    <p className="mt-4 text-[10px] font-black uppercase tracking-widest opacity-50">Syncing Node</p>
  </div>
);

// GUARDS
function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return (user?.role === 'admin' || user?.email === 'mtlhumaid@gmail.com' || user?.id) ? children : <Navigate to="/dashboard" replace />;
}

function RootOnlyRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  return (isAuthenticated && user?.id) ? children : <Navigate to="/dashboard" replace />;
}

function RedirectIfAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
}

export default function App() {
  const location = useLocation();
  const { user } = useAuth(); 
  
  useEffect(() => {
    if (window.location.pathname === '/rss.xml' || window.location.pathname === '/api/rss.xml') {
      window.location.href = window.location.pathname;
    }
  }, []);

  const isAdminPath = location.pathname.startsWith('/admin');
  const isCameraPath = location.pathname === '/camera';
  const isTrackingPath = location.pathname.startsWith('/track') || location.pathname.startsWith('/buyer-tracking') || location.pathname.startsWith('/tracking');
  const isFullscreenApp = isCameraPath || isTrackingPath;

  const [showMonitor, setShowMonitor] = useState(false);

  useEffect(() => {
    if ("Notification" in window) {
      Notification.requestPermission();
    }

    const syncOmniEngine = async () => {
      try {
        const response = await fetch('https://wiredan-pulse-engine.mtlhumaid.workers.dev/api/encyclopedia');
        if (response.ok) {
          console.log("Omni-Engine: Sync Successful");
        }
      } catch (err) {
        console.warn("Omni-Engine: Handshake standby");
      }
    };
    syncOmniEngine();

    window.addEventListener('appinstalled', () => {
      toast.success('Wiredan Vault installed successfully!', { icon: '🛡️' });
    });

    const initMonitoring = async () => {
      try {
        const { startGlobalMonitoring } = await import('./utils/DataValidator');
        startGlobalMonitoring();
      } catch (e) {}
    };
    initMonitoring();

    const checkTransactionUpdates = async () => {
      if (!user?.id) return;
      try {
        const data = await api.checkNotifications(user.id);
        if (data.success && data.count > 0) {
          toast.success(`Active Trade: You have ${data.count} pending updates.`, {
            duration: 6000,
            icon: '📦',
            style: { background: '#0B1221', color: '#fff', fontSize: '12px' }
          });
        }
      } catch (err) {
        console.log("Vault Sync: Node standby");
      }
    };

    let pollInterval;
    if (user?.id) {
      checkTransactionUpdates();
      pollInterval = setInterval(checkTransactionUpdates, 60000);
    }

    window.toggleRootConsole = () => setShowMonitor(prev => !prev);
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [user]);

  return (
    <VaultProvider> 
      <div className="min-h-screen w-screen flex flex-col bg-[#F5F2F0] text-[#0B1221] overflow-x-hidden">
        {!isFullscreenApp && <GlobalBanner />}
        
        <Suspense fallback={null}>
          {user?.id && showMonitor && <ErrorMonitor />}
        </Suspense>

        {(!isAdminPath && !isFullscreenApp) && <Navbar />}
        
        <div className="flex flex-grow w-full">
          {isAdminPath && <AdminSidebar />}
          
          <main className={`flex-grow w-full ${isFullscreenApp ? 'pb-0 h-screen overflow-hidden' : (isAdminPath ? 'pb-0' : 'pb-20')}`}>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/test-frontend" element={<FrontendDebug />} />
                
                <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                
                <Route path="/login" element={<RedirectIfAuth><LoginPage /></RedirectIfAuth>} />
                <Route path="/signup" element={<RedirectIfAuth><SignupPage /></RedirectIfAuth>} />
                
                <Route path="/camera" element={<PrivateRoute><DanAiCamera /></PrivateRoute>} />
                
                {/* 🎵 Music Studio Route Protected via WiredanHubGateway Subscription Paywall */}
                <Route path="/music-studio" element={<PrivateRoute><WiredanHubGateway /></PrivateRoute>} />
                
                <Route path="/logistics" element={<PrivateRoute><LogisticsDashboard /></PrivateRoute>} />
                <Route path="/logistics/:orderId" element={<PrivateRoute><LogisticsDashboard /></PrivateRoute>} />

                <Route path="/tracking/:orderId" element={<PrivateRoute><BuyerTrackingMap /></PrivateRoute>} />
                <Route path="/track/:orderId" element={<PrivateRoute><BuyerTrackingMap /></PrivateRoute>} />
                <Route path="/buyer-tracking/:orderId" element={<PrivateRoute><BuyerTrackingMap /></PrivateRoute>} />

                <Route path="/education-hub" element={<EducationHub />} />
                <Route path="/marketplace" element={<TradeMarketplace />} />
                <Route path="/global-market" element={<GlobalMarket user={user} />} />
                <Route path="/local-market" element={<TradeMarketplace mode="local" />} />

                <Route path="/vault/:id" element={<PrivateRoute><VaultOrder /></PrivateRoute>} />
                <Route path="/vault/order/:id" element={<PrivateRoute><VaultOrder /></PrivateRoute>} />
                <Route path="/vault/escrow/:id" element={<PrivateRoute><VaultOrder /></PrivateRoute>} />
                <Route path="/vault/local/:orderId" element={<PrivateRoute><LocalVault /></PrivateRoute>} />
                <Route path="/vault/global/:orderId" element={<PrivateRoute><GlobalVault /></PrivateRoute>} />
                <Route path="/vault/order-details/:orderId" element={<PrivateRoute><OrderDetails /></PrivateRoute>} />

                <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
                <Route path="/setting" element={<PrivateRoute><Settings /></PrivateRoute>} />
                <Route path="/node" element={<Navigate to="/setting" replace />} />
                <Route path="/transactions" element={<PrivateRoute><TransactionHistory /></PrivateRoute>} />
                <Route path="/seller/dashboard" element={<PrivateRoute><SellerDashboard /></PrivateRoute>} />
                <Route path="/kyc" element={<Navigate to="/identity-verification" replace />} />
                <Route path="/identity-verification" element={<PrivateRoute><IdentityVerification /></PrivateRoute>} />
                <Route path="/marketplace/create" element={<PrivateRoute><MarketplaceCreate /></PrivateRoute>} />
                <Route path="/list-product" element={<PrivateRoute><ProductListing /></PrivateRoute>} />
                <Route path="/trade/verify" element={<PrivateRoute><TradeVerify /></PrivateRoute>} />
                <Route path="/escrow/session/:sessionId" element={<PrivateRoute><EscrowSession /></PrivateRoute>} />
                
                <Route path="/agri/setup" element={<PrivateRoute><AgriSetup /></PrivateRoute>} />

                <Route path="/orders" element={<PrivateRoute><OrderTracking /></PrivateRoute>} />
                <Route path="/order-tracking" element={<PrivateRoute><OrderTracking /></PrivateRoute>} />
                <Route path="/order-tracking/:id" element={<PrivateRoute><OrderTracking /></PrivateRoute>} />
                
                <Route path="/dispute/:orderId" element={<PrivateRoute><Dispute /></PrivateRoute>} />
                
                <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin/dashboard" element={<AdminRoute><RootDashboard /></AdminRoute>} />
                <Route path="/admin/disputes" element={<AdminRoute><DisputeCenter /></AdminRoute>} />
                <Route path="/admin/nexus" element={<AdminRoute><AdminNexus /></AdminRoute>} />
                <Route path="/admin/audit" element={<AdminRoute><ActivityLog /></AdminRoute>} /> 
                <Route path="/admin/system-health" element={<AdminRoute><SystemHealth /></AdminRoute>} />
                <Route path="/admin/vault" element={<AdminRoute><AdminVault /></AdminRoute>} />
                <Route path="/admin/revenue" element={<RootOnlyRoute><AdminRevenue /></RootOnlyRoute>} />
                
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </main>
        </div>
        
        {(!isAdminPath && !isFullscreenApp) && <BottomNav />}
        {(!isAdminPath && !isFullscreenApp) && <Footer />}
      </div>
    </VaultProvider>
  );
}